import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from '../common/primitives.js';
import { memberRefSchema } from '../governance/topics.js';
import { obligationCategorySchema } from '../obligations/obligations.js';
import { taskSummarySchema } from '../tasks/tasks.js';
import { reportingPeriodSchema, reportStateSchema } from '../updates/updates.js';
export const reportWindowQuerySchema = z
    .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, {
    message: '"from" must not be after "to"',
    path: ['from'],
});
/**
 * Execution overview (spec §7.8). Counts only include items the reader may see. Owners are listed
 * alphabetically: the dashboard shows facts side by side and never ranks people.
 */
export const ownerWorkloadSchema = z.object({
    member: memberRefSchema,
    open: z.number().int(),
    overdue: z.number().int(),
    blocked: z.number().int(),
    inReview: z.number().int(),
    completedInWindow: z.number().int(),
});
export const blockerAgingBucketSchema = z.object({
    label: z.string(),
    minHours: z.number().int(),
    /** `null` = open-ended. */
    maxHours: z.number().int().nullable(),
    count: z.number().int(),
});
export const agingBlockerSchema = z.object({
    id: uuidSchema,
    description: z.string(),
    /** Label of the blocked task/topic, or `null` when the reader cannot see it. */
    subject: z.string().nullable(),
    link: z.string().nullable(),
    blockedMember: memberRefSchema,
    requestedFrom: z.string(),
    sinceAt: isoDateTimeSchema,
    ageHours: z.number().int(),
    escalationLevel: z.number().int(),
});
export const updateHistoryRowSchema = z.object({
    member: memberRefSchema,
    cells: z.array(z.object({ periodId: uuidSchema, state: reportStateSchema })),
});
export const executionDashboardSchema = z.object({
    asOf: isoDateTimeSchema,
    from: isoDateSchema,
    to: isoDateSchema,
    timezone: z.string(),
    owners: z.array(ownerWorkloadSchema),
    totals: z.object({
        open: z.number().int(),
        overdue: z.number().int(),
        blocked: z.number().int(),
        completedInWindow: z.number().int(),
        openBlockers: z.number().int(),
    }),
    overdueTasks: z.array(taskSummarySchema),
    blockerAging: z.object({
        buckets: z.array(blockerAgingBucketSchema),
        oldest: z.array(agingBlockerSchema),
    }),
    /** `null` when the reader cannot read weekly updates or no schedule is configured. */
    updateHistory: z
        .object({
        periods: z.array(reportingPeriodSchema),
        rows: z.array(updateHistoryRowSchema),
    })
        .nullable(),
});
export const CONTRIBUTION_KINDS = [
    'TASK_COMPLETED',
    'UPDATE_SUBMITTED',
    'OBLIGATION_PERIOD',
    'DECISION_OWNED',
    'BLOCKER_RESOLVED',
];
export const contributionKindSchema = z.enum(CONTRIBUTION_KINDS);
export const contributionQuerySchema = z
    .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    /** Obligation category or the topic category slug of linked work. */
    category: z.string().trim().min(1).max(64).optional(),
    q: z.string().trim().min(2).max(200).optional(),
    kind: contributionKindSchema.optional(),
})
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, {
    message: '"from" must not be after "to"',
    path: ['from'],
});
/** One piece of qualitative evidence (spec §7.8: "contribution timeline using qualitative evidence"). */
export const contributionEntrySchema = z.object({
    kind: contributionKindSchema,
    occurredAt: isoDateTimeSchema,
    /** Monday of the ISO week in the workspace time zone, for grouping by week. */
    weekStart: isoDateSchema,
    title: z.string(),
    detail: z.string().nullable(),
    link: z.string().nullable(),
    category: z.string().nullable(),
    evidenceUrl: z.string().nullable(),
    /** Status words such as COMPLETED / MISSED for obligation periods. */
    outcome: z.string().nullable(),
});
export const commitmentComparisonSchema = z.object({
    obligationId: uuidSchema,
    commitment: z.string(),
    category: obligationCategorySchema,
    periods: z.number().int(),
    completed: z.number().int(),
    missed: z.number().int(),
    excused: z.number().int(),
    open: z.number().int(),
    /** Weekly updates in the window that reported against this obligation. */
    reportedInUpdates: z.number().int(),
});
export const contributionHistorySchema = z.object({
    founder: z.object({
        profileId: uuidSchema,
        memberId: uuidSchema.nullable(),
        displayName: z.string(),
    }),
    from: isoDateSchema,
    to: isoDateSchema,
    timezone: z.string(),
    entries: z.array(contributionEntrySchema),
    /** Committed vs reported per obligation; `null` when the reader may not see this founder's obligations. */
    commitments: z.array(commitmentComparisonSchema).nullable(),
    /** Entries were capped; narrow the window to see everything. */
    truncated: z.boolean(),
});
//# sourceMappingURL=reports.js.map